import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { ArrowLeft, Building, Plus, Search, MoreHorizontal, Edit, Trash2, Check, ChevronsUpDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ClasificadorInmuebles = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [codigosPresupuestarios, setCodigosPresupuestarios] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/catastro/clasificador-inmuebles`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  const fetchCodigosPresupuestarios = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/codigos-presupuestarios`, { headers });
      setCodigosPresupuestarios(response.data || []);
    } catch (err) {
      console.log("Error al cargar codigos presupuestarios:", err);
    }
  };

  useEffect(() => { fetchData(); fetchCodigosPresupuestarios(); }, []);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      item.uso_inmueble?.toLowerCase().includes(s) ||
      item.codigo_presupuestario?.toLowerCase().includes(s) ||
      String(item.alicuota_anual).includes(s)
    );
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/catastro/clasificador-inmuebles`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/catastro/clasificador-inmuebles/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/clasificador-inmuebles/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center"><Building className="w-6 h-6 text-indigo-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Clasificador Inmuebles</h1>
          <p className="text-slate-500">Gestión de clasificador inmuebles</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredData.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{data.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Button onClick={() => { setEditingItem(null); setFormData({}); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo"><Plus className="w-4 h-4 mr-2" />Nuevo Registro</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Uso del Inmueble</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Alícuota Anual (%)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cód. Presupuestario</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Extranet</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Descuento</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.uso_inmueble}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.alicuota_anual}%</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.codigo_presupuestario || "-"}</td>
                      <td className="px-4 py-3 text-center text-sm">{item.extranet ? "Sí" : "No"}</td>
                      <td className="px-4 py-3 text-center text-sm">{item.descuento ? `${item.porcentaje_descuento}%` : "No"}</td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingItem(item); setFormData(item); setDialogOpen(true); }}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingItem ? "Editar Clasificador Inmuebles" : "Nuevo Registro - Clasificador Inmuebles"}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Uso del Inmueble</Label>
                <Input value={formData.uso_inmueble || ""} onChange={(e) => setFormData({...formData, uso_inmueble: e.target.value})} placeholder="Ej: Residencial, Comercial, Industrial" data-testid="form-uso-inmueble" />
              </div>
              <div className="space-y-2">
                <Label>Alícuota Anual (%)</Label>
                <Input type="number" step="0.01" value={formData.alicuota_anual || ""} onChange={(e) => setFormData({...formData, alicuota_anual: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid="form-alicuota-anual" />
              </div>
              <div className="space-y-2">
                <Label>Código Presupuestario</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-codigo-presupuestario">
                      {formData.codigo_presupuestario ? (() => { const cp = codigosPresupuestarios.find(c => c.codigo_presupuestario === formData.codigo_presupuestario); return cp ? `${cp.codigo_presupuestario} - ${cp.nombre_partida || ""}` : formData.codigo_presupuestario; })() : <span className="text-slate-500">Seleccione código</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[450px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar código o partida..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron códigos</CommandEmpty>
                        <CommandGroup>
                          {codigosPresupuestarios.map(cp => (
                            <CommandItem key={cp.id || cp.codigo_presupuestario} value={`${cp.codigo_presupuestario} ${cp.nombre_partida || ""}`} onSelect={() => setFormData({...formData, codigo_presupuestario: cp.codigo_presupuestario})}>
                              <Check className={`mr-2 h-4 w-4 ${formData.codigo_presupuestario === cp.codigo_presupuestario ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-mono text-xs">{cp.codigo_presupuestario}</span>
                              <span className="ml-2 text-xs text-slate-500 truncate">{cp.nombre_partida || ""}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Extranet</Label>
                <Select value={formData.extranet ? "si" : "no"} onValueChange={(v) => setFormData({...formData, extranet: v === "si"})}>
                  <SelectTrigger data-testid="form-extranet"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descuento</Label>
                <Select value={formData.descuento ? "si" : "no"} onValueChange={(v) => { const has = v === "si"; setFormData({...formData, descuento: has, ...(!has ? { porcentaje_descuento: 0, fecha_descuento_desde: "", fecha_descuento_hasta: "" } : {})}); }}>
                  <SelectTrigger data-testid="form-descuento-toggle"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {formData.descuento && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="space-y-2">
                  <Label>Porcentaje de Descuento (%)</Label>
                  <Input type="number" step="0.01" value={formData.porcentaje_descuento || ""} onChange={(e) => setFormData({...formData, porcentaje_descuento: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right max-w-[200px]" data-testid="form-porcentaje-descuento" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha de Descuento - Desde</Label>
                    <Input type="date" value={formData.fecha_descuento_desde || ""} onChange={(e) => setFormData({...formData, fecha_descuento_desde: e.target.value})} data-testid="form-fecha-descuento-desde" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Descuento - Hasta</Label>
                    <Input type="date" value={formData.fecha_descuento_hasta || ""} onChange={(e) => setFormData({...formData, fecha_descuento_hasta: e.target.value})} data-testid="form-fecha-descuento-hasta" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClasificadorInmuebles;
